import platform from '@tencent/wphk-platform-adapter';
import compareVersions from 'compare-versions';

const DEFAULT_VERSION = '0.0.0';

/**
 * 只保留 major.minor.patch 三段，后面 build 等丢弃
 * @param ver 版本号
 * @returns 三段 major.minor.patch
 */
const formatWeixinVer = (ver: string): string => ver.split('.').slice(0, 3)
  .join('.');

export const getUserAgent = (): string => platform.navigator.userAgent || '';

/**
 * 获取OS信息
 */
export const getOSInfoObj = (): { type: 'iphone' | 'android' | 'harmony' | 'none', version: string } => {
  let pattern = /(iPhone OS |iPad; CPU OS )([\d_]+)/i;
  let wc = getUserAgent().match(pattern);
  if (wc && wc.length >= 3) return { type: 'iphone', version: wc[2]?.replace(/_/g, '.') || DEFAULT_VERSION };
  pattern = /Android ([\d.]+)/i;
  wc = getUserAgent().match(pattern);
  if (wc && wc.length >= 2) return { type: 'android', version: wc[1] || DEFAULT_VERSION };
  pattern = /OpenHarmony[\s-]+([\d.]+)/i;
  wc = getUserAgent().match(pattern);
  if (wc && wc.length >= 2) return { type: 'harmony', version: wc[1] || DEFAULT_VERSION };
  return { type: 'none', version: DEFAULT_VERSION };
};

/** 获取系统信息文本 */
export const getOSInfoText = () => {
  const os = getOSInfoObj();
  return `${os.type} ${os.version}`;
};

/**
 * 获取微信版本
 */
export const getWeixinVer = (): string => {
  const match = getUserAgent().match(/MicroMessenger\/([\d.]+)/i);
  return match && match[1] ? formatWeixinVer(match[1]) : DEFAULT_VERSION;
};

/**
 * 比较版本1和版本2的高低, 0 相等，-1 v1 < v2， 1 v1 > v2
 * @param v1 版本1(格式类似7.0.12)
 * @param v2 版本2(格式类似7.0.12)
 */
export const verCompare = (v1: string, v2: string) => {
  // 兜底处理，如果没有传v1或v2，则默认是0
  const formatV1 = v1 || '0';
  const formatV2 = v2 || '0';
  return compareVersions(formatV1, formatV2);
};

/**
 * 比较微信版本大于等于ver
 * @param ver 比较的版本
 */
export const isWexinVerLargeOrEqualThan = (ver: string) => {
  const curVer = getWeixinVer();
  return verCompare(curVer, ver) > -1;
};


/**
 * 判断安卓、ios的版本是否大于等于指定版本
 * 如果对android系统没版本要求，则传入如 android: '0.0.0'
 * @param minVer 两个系统的最低版本
 */
export const isOSVerLargeOrEqualThan = (minVer: { android?: string; iphone?: string; harmony?: string; }) => {
  const { type, version } = getOSInfoObj();
  if (type === 'none') {
    return false;
  }
  const ver = minVer[type] || DEFAULT_VERSION;
  return verCompare(version, ver) > -1;
};

/**
 * 判断在所有的系统下最小要求支持的微信版本
 * @param minVersions 微信最小版本号（需要指定每个系统下的版本号，如：iphone: "8.0.20"，android:"8.0.21",如果对版本没要求，可传入0.0.0）
 * @returns
 */
export const isAllWxVersionSupported = (minVersions: { iphone?: string, android?: string, harmony?: string }) => {
  const { type } = getOSInfoObj();
  if (type === 'none') {
    return false;
  }
  return isWexinVerLargeOrEqualThan(minVersions[type] || DEFAULT_VERSION);
};

/** 判断是否在微信浏览器内 */
export const isInWeixin = () => /MicroMessenger/i.test(getUserAgent());

/**
 * 判断是否在小程序内
 */
// @ts-ignore
export const isInMiniProgram = () => /miniprogram/i.test(getUserAgent()) || window.__wxjs_environment === 'miniprogram';

/**
 * H5 被小程序内嵌时，获取宿主小程序appId
 */
export const getMiniProgramAppId = () => {
  const ua = getUserAgent();
  const pattern = /miniProgram\/(\w+)/;
  const result = pattern.exec(ua);
  if (result?.[1]) {
    return result[1];
  }
  return '';
};

/**
 * 将hex版本号转为描述版本号，如0x28001320 => 8.0.19。
 *
 * iOS/Android 格式 (Platform 1位):
 *   0x [P] [Major(1)] [Minor(2)] [Patch(2)] [Build(2)]
 *   iOS:     0x10000000 - 0x1FFFFFFF  (P=1)
 *   Android: 0x20000000 - 0x2FFFFFFF  (P=2)
 *   例: 0x18001320 => 8.0.19
 *
 * OHOS 格式 (Platform 2位):
 *   0x [F3] [Major(1)] [Minor(1)] [Patch(2)] [Build(2)]
 *   OHOS: 0xF3000000 - 0xF3FFFFFF  (P=F3)
 *   例: 0xF3101538 => 1.0.21
 *
 * @param hexVersion hex版本号：如0x28003d37
 */
export const hexWxVersionToDesc = (hexVersion: number): string => {
  const hexVersionStr = hexVersion.toString(16);
  const ohosRegex = /^f3([0-9a-f])([0-9a-f])([0-9a-f]{2})([0-9a-f]{2})$/i;
  const regex = /([0-9a-f])([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
  const ms = hexVersionStr.match(ohosRegex) || hexVersionStr.match(regex);
  if (ms) {
    const result = ms
      .filter((_, index) => index > 0)
      .map(item => parseInt(item, 16))
      .join('.');
    return formatWeixinVer(result);
  }
  return DEFAULT_VERSION;
};
